import React, { useState } from 'react';
import { local_signage, local_url } from '../../../Const';
import Header from '../../../Header';

/**
 * WalletReportCompact - A compact, responsive React component for wallet report
 *
 * Features:
 * - Search by CustId or Mobile
 * - Date range filtering
 * - Grouped table by business date with consumption and purchase data
 * - CSV export
 * - Fully responsive with inline styles and Tailwind CSS
 *
 * API: https://retailuat.abisibg.com/api/v1/walletreport
 */

// ============ Inline Styles ============
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)',
    padding: '0.5rem',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '100vh',
  },
  maxWidth: {
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '0.75rem',
    paddingBottom: '0.5rem',
    borderBottom: '2px solid #e5e7eb',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '0.25rem',
  },
  subtitle: {
    fontSize: '0.75rem',
    color: '#6b7280',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '0.375rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    padding: '0.875rem',
    marginBottom: '0.75rem',
    border: '1px solid #e5e7eb',
  },
  formSection: {
    marginBottom: '0.75rem',
  },
  label: {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.375rem',
  },
  radioGroup: {
    display: 'flex',
    gap: '1.25rem',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '0.375rem 0.625rem',
    fontSize: '0.8125rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.3125rem',
    outline: 'none',
    transition: 'all 0.3s',
  },
  inputFocus: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  gridMd: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  button: {
    padding: '0.375rem 0.875rem',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '0.8125rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '0.3125rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.375rem',
  },
  buttonHover: {
    backgroundColor: '#1d4ed8',
  },
  buttonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '0.3125rem',
    padding: '0.625rem',
    marginBottom: '0.75rem',
  },
  errorText: {
    fontSize: '0.8125rem',
    color: '#dc2626',
    fontWeight: '500',
  },
  customerInfo: {
    backgroundColor: '#eff6ff',
    border: '1px solid #93c5fd',
    borderRadius: '0.375rem',
    padding: '0.75rem',
    marginBottom: '0.75rem',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.75rem',
    fontSize: '0.8125rem',
  },
  infoLabel: {
    fontWeight: '600',
    color: '#1e40af',
  },
  infoValue: {
    color: '#0c4a6e',
    fontWeight: '500',
  },
  exportButton: {
    padding: '0.375rem 0.875rem',
    backgroundColor: '#16a34a',
    color: '#ffffff',
    fontSize: '0.8125rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '0.3125rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    marginBottom: '0.75rem',
    marginLeft: 'auto',
  },
  exportButtonHover: {
    backgroundColor: '#15803d',
  },
  tableContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '0.375rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  thead: {
    backgroundColor: '#f3f4f6',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  th: {
    padding: '0.5rem 0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '700',
    color: '#1f2937',
    borderBottom: '1.5px solid #d1d5db',
    whiteSpace: 'nowrap',
    letterSpacing: '0.3px',
  },
  thCenter: {
    textAlign: 'center',
  },
  td: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.8125rem',
    color: '#1f2937',
    borderBottom: '1px solid #e5e7eb',
  },
  tdCenter: {
    textAlign: 'center',
  },
  tbody: {
    cursor: 'pointer',
  },
  trHover: {
    backgroundColor: '#f3f4f6',
  },
  tfoot: {
    backgroundColor: '#f9fafb',
    borderTop: '2px solid #d1d5db',
    fontWeight: 'bold',
  },
  emptyState: {
    padding: '1.5rem',
    textAlign: 'center',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '0.8125rem',
  },
};

// ============ Helper Functions ============

/**
 * Normalize date string to YYYY-MM-DD format
 * Handles ISO strings and various date formats
 */
const normalizeDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

/**
 * Format date from HTML input (YYYY-MM-DD) to API format (YYYYMMDD)
 */
const formatDateForAPI = (dateString) => {
  if (!dateString) return '';
  return dateString.replace(/-/g, '');
};

/**
 * Convert API date format (YYYYMMDD) to display format (YYYY-MM-DD)
 */
const formatDateFromAPI = (dateString) => {
  if (!dateString || dateString.length !== 8) return dateString;
  return `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`;
};

/**
 * Extract detailed transactions for a specific date
 * Returns { consume: [...], purchase: [...] }
 */
const getTransactionDetails = (walletArray, date) => {
  const details = { consume: [], purchase: [] };

  if (!Array.isArray(walletArray)) return details;

  walletArray.forEach((wallet) => {
    // Extract consume details
    if (Array.isArray(wallet.consume)) {
      wallet.consume.forEach((item) => {
        const dateValue = item.Businessdate || item.BusinessDate || item.businessDate || item.businessdate;
        const itemDate = normalizeDate(dateValue);
        if (itemDate === date) {
          details.consume.push({
            ...item,
            CustomerName: wallet.CustomerName,
            CustID: wallet.CustID,
          });
        }
      });
    }

    // Extract purchase details
    if (Array.isArray(wallet.purchase)) {
      wallet.purchase.forEach((item) => {
        const dateValue = item.BusinessDate || item.businessDate || item.Businessdate;
        const itemDate = normalizeDate(dateValue);
        if (itemDate === date) {
          details.purchase.push({
            ...item,
            CustomerName: wallet.CustomerName,
            CustID: wallet.CustID,
          });
        }
      });
    }
  });

  return details;
};

/**
 * Group and aggregate wallet data by business date and branch
 * Returns object: { date: { consumption: total, consumptionCount, purchase: total, purchaseCount, branches: Set } }
 */
const groupDataByDate = (walletArray) => {
  const grouped = {};

  if (!Array.isArray(walletArray)) {
    console.warn('walletArray is not an array:', walletArray);
    return grouped;
  }

  console.log('Starting groupDataByDate with', walletArray.length, 'wallets');

  walletArray.forEach((wallet, walletIdx) => {
    console.log(`Processing wallet ${walletIdx}:`, {
      hasPurchase: Array.isArray(wallet.purchase),
      purchaseCount: wallet.purchase ? wallet.purchase.length : 0,
      hasConsume: Array.isArray(wallet.consume),
      consumeCount: wallet.consume ? wallet.consume.length : 0,
    });

    // Process purchases
    if (Array.isArray(wallet.purchase)) {
      wallet.purchase.forEach((item, purchaseIdx) => {
        // Handle BusinessDate field (try multiple variations)
        const dateValue = item.BusinessDate || item.businessDate || item.Businessdate;
        const date = normalizeDate(dateValue);

        if (!date) {
          console.warn(`Purchase ${purchaseIdx} - Could not normalize date:`, {
            dateValue,
            item,
          });
        }

        if (date) {
          if (!grouped[date]) {
            grouped[date] = {
              consumption: 0,
              consumptionCount: 0,
              purchase: 0,
              purchaseCount: 0,
              branches: new Set(),
            };
          }
          const amount = parseFloat(item.collAmount) || 0;
          const branchId = item.BranchID || 'N/A';
          grouped[date].purchase += amount;
          grouped[date].purchaseCount += 1;
          grouped[date].branches.add(branchId);
          console.log(`Purchase added to ${date}: +${amount} (Branch: ${branchId})`);
        }
      });
    }

    // Process consumption - Note: API uses 'Businessdate' (lowercase 'date')
    if (Array.isArray(wallet.consume)) {
      console.log(`Processing ${wallet.consume.length} consume items`);
      wallet.consume.forEach((item, consumeIdx) => {
        // Handle all possible date field variations
        const dateValue = item.Businessdate || item.BusinessDate || item.businessDate || item.businessdate;

        console.log(`Consume item ${consumeIdx}:`, {
          Businessdate: item.Businessdate,
          BusinessDate: item.BusinessDate,
          AmountUsed: item.AmountUsed,
          BranchId: item.BranchId,
          dateValue,
        });

        const date = normalizeDate(dateValue);

        if (!date) {
          console.warn(`Consume ${consumeIdx} - Could not normalize date:`, {
            dateValue,
            item,
          });
        }

        if (date) {
          if (!grouped[date]) {
            grouped[date] = {
              consumption: 0,
              consumptionCount: 0,
              purchase: 0,
              purchaseCount: 0,
              branches: new Set(),
            };
          }
          const amount = parseFloat(item.AmountUsed) || 0;
          const branchId = item.BranchId || 'N/A';
          grouped[date].consumption += amount;
          grouped[date].consumptionCount += 1;
          grouped[date].branches.add(branchId);
          console.log(`Consumption added to ${date}: +${amount} (Branch: ${branchId})`);
        }
      });
    }
  });

  console.log('Final grouped data:', grouped);
  return grouped;
};

/**
 * Export table data to CSV
 */
const exportToCSV = (customerInfo, groupedData, searchType, searchValue) => {
  const rows = [];

  // Header with customer info
  rows.push(['WALLET REPORT']);
  rows.push([]);
  rows.push(['Customer Name', customerInfo.customerName || '-']);
  rows.push(['Customer ID', customerInfo.custId || '-']);
  rows.push(['Mobile', customerInfo.mobile || '-']);
  rows.push(['Search Type', searchType]);
  rows.push(['Search Value', searchValue]);
  rows.push([]);

  // Table header
  rows.push(['Business Date', 'Branch ID', 'Consumption Amount', 'Consumption Count', 'Purchase Amount', 'Purchase Count']);

  // Data rows
  const sortedDates = Object.keys(groupedData).sort();
  let totalConsumption = 0;
  let totalPurchase = 0;

  sortedDates.forEach((date) => {
    const data = groupedData[date];
    const branches = Array.from(data.branches).sort().join(', ');
    rows.push([
      date,
      branches,
      data.consumption,
      data.consumptionCount,
      data.purchase,
      data.purchaseCount,
    ]);
    totalConsumption += data.consumption;
    totalPurchase += data.purchase;
  });

  // Totals row
  rows.push([]);
  rows.push(['TOTAL', '', totalConsumption, '', totalPurchase, '']);

  // Convert to CSV string
  const csvContent = rows.map((row) =>
    row.map((cell) => {
      const cellStr = String(cell);
      // Escape quotes and wrap in quotes if contains comma or quote
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    }).join(',')
  ).join('\n');

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `WalletReport_${Date.now()}.csv`;
  link.click();
};

// ============ Main Component ============

export const  WalletReportCompact=()=> {
  // State for search inputs
  const [searchType, setSearchType] = useState('CustId');
  const [searchValue, setSearchValue] = useState('L108 116');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // State for API data and loading
  const [walletData, setWalletData] = useState(null);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // State for customer info header
  const [customerInfo, setCustomerInfo] = useState({
    customerName: '',
    custId: '',
    mobile: '',
  });

  // State for drill-down
  const [expandedDate, setExpandedDate] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);

  /**
   * Validate inputs before API call
   */
  const validateInputs = () => {
    if (!searchValue.trim()) {
      setError(`Please enter a ${searchType === 'CustId' ? 'Customer ID' : 'Mobile number'}`);
      return false;
    }

    if (startDate && endDate && startDate > endDate) {
      setError('Start date cannot be after end date');
      return false;
    }

    setError('');
    return true;
  };

  /**
   * Fetch wallet data from API
   */
  const handleFetchData = async () => {
    if (!validateInputs()) return;

    setLoading(true);
    setError('');

    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (searchType === 'CustId') {
        params.append('CustId', searchValue);
        params.append('Mobile', 'ALL');
      } else {
        params.append('CustId', 'ALL');
        params.append('Mobile', searchValue);
      }

      if (startDate) {
        params.append('StartDate', formatDateForAPI(startDate));
      }
      if (endDate) {
        params.append('EndDate', formatDateForAPI(endDate));
      }

      const url = `${local_signage}/api/v1/walletreport?${params.toString()}`;

      // Get authorization token from localStorage
      const token = localStorage.getItem('AuthorizationToken');

      // Validate token exists
      if (!token) {
        setError('Authorization token not found. Please login again.');
        setLoading(false);
        return;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Unauthorized: Your session has expired. Please login again.');
        } else if (response.status === 403) {
          setError('Forbidden: You do not have permission to access this data.');
        } else {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!data.Wallet || data.Wallet.length === 0) {
        setError('No data found for the selected criteria');
        setGroupedData({});
        setCustomerInfo({});
        setWalletData(null);
        return;
      }

      // Extract first wallet record for customer info
      const firstWallet = data.Wallet[0];
      setCustomerInfo({
        customerName: firstWallet.CustomerName || '',
        custId: firstWallet.CustID || '',
        mobile: firstWallet.Mobile || '',
      });

      // Group data by date
      const grouped = groupDataByDate(data.Wallet);

      // Debug: Log the grouped data to console
      console.log('API Response:', data);
      console.log('Grouped Data:', grouped);
      console.log('Wallet Array Length:', data.Wallet.length);
      data.Wallet.forEach((wallet, idx) => {
        console.log(`Wallet ${idx}:`, {
          CustID: wallet.CustID,
          consume: wallet.consume ? wallet.consume.length : 0,
          purchase: wallet.purchase ? wallet.purchase.length : 0,
        });
      });

      setGroupedData(grouped);
      setWalletData(data);
    } catch (err) {
      setError(`Failed to fetch data: ${err.message}`);
      setGroupedData({});
      setWalletData(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle row expansion to show transaction details
   */
  const handleRowClick = (date) => {
    if (expandedDate === date) {
      // Collapse if clicking the same row
      setExpandedDate(null);
      setTransactionDetails(null);
    } else {
      // Expand to show details
      const details = getTransactionDetails(walletData?.Wallet || [], date);
      setExpandedDate(date);
      setTransactionDetails(details);
    }
  };

  /**
   * Handle Enter key press in inputs
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFetchData();
    }
  };

  // Calculate totals
  const sortedDates = Object.keys(groupedData).sort();
  const totals = {
    consumption: sortedDates.reduce((sum, date) => sum + groupedData[date].consumption, 0),
    purchase: sortedDates.reduce((sum, date) => sum + groupedData[date].purchase, 0),
  };

  const [focusedInput, setFocusedInput] = useState(null);

  return (
    <div style={styles.container}>
      <div style={styles.maxWidth}>
        {/* Header */}
        <div style={styles.header}>
          <Header/>
        </div>

        {/* Search Card */}
        <div style={styles.card}>
          {/* Search Type Radio Buttons */}
          <div style={styles.formSection}>
            <label style={styles.label}>Search Type</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="searchType"
                  value="CustId"
                  checked={searchType === 'CustId'}
                  onChange={(e) => {
                    setSearchType(e.target.value);
                    setSearchValue('L108 116');
                    setError('');
                  }}
                  style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#2563eb' }}
                  aria-label="Search by Customer ID"
                />
                <span style={{ marginLeft: '0.375rem', fontSize: '0.8125rem', color: '#374151' }}>Customer ID</span>
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="searchType"
                  value="Mobile"
                  checked={searchType === 'Mobile'}
                  onChange={(e) => {
                    setSearchType(e.target.value);
                    setSearchValue('');
                    setError('');
                  }}
                  style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#2563eb' }}
                  aria-label="Search by Mobile number"
                />
                <span style={{ marginLeft: '0.375rem', fontSize: '0.8125rem', color: '#374151' }}>Mobile Number</span>
              </label>
            </div>
          </div>

          {/* Search Input and Dates Grid */}
          <div style={styles.gridMd}>
            {/* Search Input */}
            <div>
              <label style={styles.label}>
                {searchType === 'CustId' ? 'Customer ID' : 'Mobile Number'}
              </label>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => setFocusedInput('search')}
                onBlur={() => setFocusedInput(null)}
                placeholder={
                  searchType === 'CustId'
                    ? 'e.g. L108 116'
                    : 'e.g. 9156853295'
                }
                style={{
                  ...styles.input,
                  ...(focusedInput === 'search' ? styles.inputFocus : {}),
                }}
                aria-label={searchType === 'CustId' ? 'Customer ID input' : 'Mobile number input'}
              />
            </div>

            {/* Start Date */}
            <div>
              <label style={styles.label}>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setError('');
                }}
                onFocus={() => setFocusedInput('startDate')}
                onBlur={() => setFocusedInput(null)}
                style={{
                  ...styles.input,
                  ...(focusedInput === 'startDate' ? styles.inputFocus : {}),
                }}
                aria-label="Start date picker"
              />
            </div>

            {/* End Date */}
            <div>
              <label style={styles.label}>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setError('');
                }}
                onFocus={() => setFocusedInput('endDate')}
                onBlur={() => setFocusedInput(null)}
                style={{
                  ...styles.input,
                  ...(focusedInput === 'endDate' ? styles.inputFocus : {}),
                }}
                aria-label="End date picker"
              />
            </div>

            {/* Fetch Button */}
            <div style={{ display: 'flex', alignItems: 'flex-end', minHeight: '48px' }}>
              <button
                onClick={handleFetchData}
                disabled={loading}
                onMouseEnter={(e) => {
                  if (!loading) e.target.style.backgroundColor = styles.buttonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.target.style.backgroundColor = styles.button.backgroundColor;
                }}
                style={{
                  ...styles.button,
                  ...(loading ? styles.buttonDisabled : {}),
                  padding: '0.375rem 0.875rem',
                  fontSize: '0.8125rem',
                }}
                aria-label="Fetch wallet data"
              >
                {loading ? (
                  <>
                    <svg style={{ animation: 'spin 1s linear infinite', width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Fetching...</span>
                  </>
                ) : (
                  'Fetch'
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={styles.errorBox}>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}
        </div>

        {/* Results Section */}
        {Object.keys(groupedData).length > 0 && (
          <>
            {/* Customer Info Header */}
            <div style={styles.customerInfo}>
              <div style={styles.infoGrid}>
                <div>
                  <span style={styles.infoLabel}>Customer: </span>
                  <span style={styles.infoValue}>{customerInfo.customerName || '-'}</span>
                </div>
                <div>
                  <span style={styles.infoLabel}>Cust ID: </span>
                  <span style={styles.infoValue}>{customerInfo.custId || '-'}</span>
                </div>
                <div>
                  <span style={styles.infoLabel}>Mobile: </span>
                  <span style={styles.infoValue}>{customerInfo.mobile || '-'}</span>
                </div>
              </div>
            </div>

            {/* Export Button */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => exportToCSV(customerInfo, groupedData, searchType, searchValue)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = styles.exportButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = styles.exportButton.backgroundColor;
                }}
                style={styles.exportButton}
                aria-label="Export data to CSV"
              >
                <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8m0 8l-4-2m4 2l4-2" />
                </svg>
                Export CSV
              </button>
            </div>

            {/* Table Container */}
            <div style={styles.tableContainer}>
              {/* Responsive Table */}
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead style={styles.thead}>
                    <tr>
                      <th style={{ ...styles.th }}>
                        Business Date
                      </th>
                      <th style={{ ...styles.th }}>
                        Branch ID
                      </th>
                      <th style={{ ...styles.th, ...styles.thCenter }}>
                        Consumption
                      </th>
                      <th style={{ ...styles.th, ...styles.thCenter }}>
                        Purchase
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDates.map((date) => {
                      const data = groupedData[date];
                      const branches = Array.from(data.branches).sort().join(', ');
                      const isExpanded = expandedDate === date;
                      return (
                        <React.Fragment key={date}>
                          <tr
                            onClick={() => handleRowClick(date)}
                            style={{
                              ...styles.tbody,
                              borderBottom: '1px solid #e5e7eb',
                              cursor: 'pointer',
                              backgroundColor: isExpanded ? '#dbeafe' : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                              if (!isExpanded) e.currentTarget.style.backgroundColor = styles.trHover.backgroundColor;
                            }}
                            onMouseLeave={(e) => {
                              if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <td style={{ ...styles.td, whiteSpace: 'nowrap', fontWeight: '500' }}>
                              <span style={{ marginRight: '0.5rem' }}>
                                {isExpanded ? '▼' : '▶'}
                              </span>
                              {date}
                            </td>
                            <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: '0.813rem' }}>
                              {branches || '-'}
                            </td>
                            <td style={{ ...styles.td, ...styles.tdCenter }}>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                                {data.consumption > 0 ? data.consumption.toLocaleString('en-IN') : '-'}
                              </div>
                              {data.consumptionCount > 0 && (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  ({data.consumptionCount} {data.consumptionCount === 1 ? 'item' : 'items'})
                                </div>
                              )}
                            </td>
                            <td style={{ ...styles.td, ...styles.tdCenter }}>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                                {data.purchase > 0 ? data.purchase.toLocaleString('en-IN') : '-'}
                              </div>
                              {data.purchaseCount > 0 && (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  ({data.purchaseCount} {data.purchaseCount === 1 ? 'item' : 'items'})
                                </div>
                              )}
                            </td>
                          </tr>

                          {/* Drill-down Details Row */}
                          {isExpanded && transactionDetails && (
                            <tr style={{ backgroundColor: '#f0f9ff', borderBottom: '1px solid #bfdbfe' }}>
                              <td colSpan="4" style={{ padding: '0.75rem' }}>
                                {/* Consumption Details */}
                                {transactionDetails.consume.length > 0 && (
                                  <div style={{ marginBottom: '0.75rem' }}>
                                    <h4 style={{ fontSize: '0.875rem', fontWeight: '700', color: '#0c4a6e', marginBottom: '0.375rem' }}>
                                      📥 Consumption ({transactionDetails.consume.length})
                                    </h4>
                                    <div style={{ overflowX: 'auto', marginBottom: '0.5rem' }}>
                                      <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                                        <thead>
                                          <tr style={{ backgroundColor: '#dbeafe', borderBottom: '1px solid #7dd3fc' }}>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#0c4a6e' }}>ID</th>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#0c4a6e' }}>Branch</th>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'right', fontWeight: '600', color: '#0c4a6e' }}>Amount</th>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#0c4a6e' }}>Type</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {transactionDetails.consume.map((item, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #e0f2fe' }}>
                                              <td style={{ padding: '0.375rem 0.5rem', color: '#0c4a6e', fontSize: '0.7rem' }}>{item.UniqueID}</td>
                                              <td style={{ padding: '0.375rem 0.5rem', color: '#0c4a6e' }}>{item.BranchId}</td>
                                              <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right', fontWeight: '600', color: '#0c4a6e' }}>
                                                {(item.AmountUsed || 0).toLocaleString('en-IN')}
                                              </td>
                                              <td style={{ padding: '0.375rem 0.5rem', color: '#0c4a6e', fontSize: '0.7rem' }}>{item.UsedbyTransType}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Purchase Details */}
                                {transactionDetails.purchase.length > 0 && (
                                  <div>
                                    <h4 style={{ fontSize: '0.875rem', fontWeight: '700', color: '#15803d', marginBottom: '0.375rem' }}>
                                      📤 Purchase ({transactionDetails.purchase.length})
                                    </h4>
                                    <div style={{ overflowX: 'auto' }}>
                                      <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                                        <thead>
                                          <tr style={{ backgroundColor: '#dcfce7', borderBottom: '1px solid #86efac' }}>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#15803d' }}>ID</th>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#15803d' }}>Branch</th>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'right', fontWeight: '600', color: '#15803d' }}>Amount</th>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#15803d' }}>Payment Gateway</th>
                                            <th style={{ padding: '0.375rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#15803d' }}>Remarks</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {transactionDetails.purchase.map((item, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f0fdf4' }}>
                                              <td style={{ padding: '0.375rem 0.5rem', color: '#15803d', fontSize: '0.7rem' }}>{item.DirectCollectionHdrid}</td>
                                              <td style={{ padding: '0.375rem 0.5rem', color: '#15803d' }}>{item.BranchID}</td>
                                              <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right', fontWeight: '600', color: '#15803d' }}>
                                                {(item.collAmount || 0).toLocaleString('en-IN')}
                                              </td>
                                              <td style={{ padding: '0.375rem 0.5rem', color: '#15803d', fontSize: '0.7rem' }}>
                                                {item.PaymentGatewayID || '-'}
                                              </td>
                                              <td style={{ padding: '0.375rem 0.5rem', color: '#15803d', fontSize: '0.7rem' }}>
                                                {item.Remarks || '-'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Empty State */}
                                {transactionDetails.consume.length === 0 && transactionDetails.purchase.length === 0 && (
                                  <p style={{ color: '#64748b', fontSize: '0.8rem' }}>No transaction data available</p>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {/* Totals Row */}
                  <tfoot style={styles.tfoot}>
                    <tr>
                      <td style={{ ...styles.td, fontWeight: 'bold' }}>TOTAL</td>
                      <td style={{ ...styles.td, fontWeight: 'bold' }}></td>
                      <td style={{ ...styles.td, ...styles.tdCenter, fontWeight: 'bold' }}>
                        {totals.consumption > 0 ? totals.consumption.toLocaleString('en-IN') : '0'}
                      </td>
                      <td style={{ ...styles.td, ...styles.tdCenter, fontWeight: 'bold' }}>
                        {totals.purchase > 0 ? totals.purchase.toLocaleString('en-IN') : '0'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* No Data Message */}
              {sortedDates.length === 0 && (
                <div style={styles.emptyState}>
                  <p style={styles.emptyText}>No data available for the selected criteria</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Initial State Message */}
        {Object.keys(groupedData).length === 0 && !loading && !error && (
          <div style={{ ...styles.card, textAlign: 'center', padding: '1.25rem' }}>
            <svg style={{ margin: '0 auto 0.75rem', width: '40px', height: '40px', color: '#d1d5db' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p style={{ color: '#9ca3af', fontSize: '0.8125rem', margin: 0 }}>Enter search criteria and click Fetch to view wallet data</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}